import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, Reveal } from "@/components/Motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT, SUBURBS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in name, email and message."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm(initial);
    } catch { toast.error("Something went wrong. Please try calling us."); }
    finally { setLoading(false); }
  };

  const waMsg = encodeURIComponent("Hi Britelines! I'd like to ask about cleaning in Adelaide.");

  return (
    <div>
      <section className="pt-40 pb-16 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <p className="kicker mb-6">Contact</p>
          <h1 className="font-display font-extrabold tracking-tight text-[#1C2A39] text-5xl sm:text-7xl leading-[0.95]">
            <MaskedLines lines={["Let's talk", "clean."]} />
          </h1>
        </div>
      </section>

      <section className="pb-24 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="space-y-4">
                <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-call" className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-slate-200 hover:border-[#0D9488]/40 transition-colors duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#E8F1EB] flex items-center justify-center"><Phone size={20} className="text-[#0D9488]" /></div>
                  <div><p className="text-xs text-[#475569]">Call us</p><p className="font-medium text-[#1C2A39]">{CONTACT.phone}</p></div>
                </a>
                <a href={`https://wa.me/${CONTACT.whatsapp}?text=${waMsg}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-slate-200 hover:border-[#25D366]/50 transition-colors duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#dcfce7] flex items-center justify-center"><MessageCircle size={20} className="text-[#25D366]" /></div>
                  <div><p className="text-xs text-[#475569]">WhatsApp</p><p className="font-medium text-[#1C2A39]">Chat with us now</p></div>
                </a>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-slate-200 hover:border-[#0D9488]/40 transition-colors duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#E8F1EB] flex items-center justify-center"><Mail size={20} className="text-[#0D9488]" /></div>
                  <div><p className="text-xs text-[#475569]">Email</p><p className="font-medium text-[#1C2A39]">{CONTACT.email}</p></div>
                </a>
                <div className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-[#E8F1EB] flex items-center justify-center"><MapPin size={20} className="text-[#0D9488]" /></div>
                  <div><p className="text-xs text-[#475569]">Visit / mail</p><p className="font-medium text-[#1C2A39]">{CONTACT.address}</p></div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.2}>
              <form data-testid="contact-form" onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-9">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2"><Label className="text-sm font-medium text-[#1C2A39]">Name *</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="bg-[#FAFAF9] h-12 rounded-xl border-slate-200" /></div>
                  <div className="flex flex-col gap-2"><Label className="text-sm font-medium text-[#1C2A39]">Email *</Label><Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-[#FAFAF9] h-12 rounded-xl border-slate-200" /></div>
                </div>
                <div className="flex flex-col gap-2 mt-5"><Label className="text-sm font-medium text-[#1C2A39]">Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-[#FAFAF9] h-12 rounded-xl border-slate-200" /></div>
                <div className="flex flex-col gap-2 mt-5"><Label className="text-sm font-medium text-[#1C2A39]">Message *</Label><Textarea data-testid="contact-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={5} className="bg-[#FAFAF9] rounded-xl border-slate-200 resize-none" /></div>
                <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center mt-7 !py-4 disabled:opacity-70">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : "Send Message"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Service area map */}
      <section data-testid="service-map" className="bg-white">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 py-20">
          <div className="max-w-xl mb-10">
            <p className="kicker mb-4">Service area</p>
            <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-tight text-[#1C2A39]">Proudly cleaning across Adelaide.</h2>
          </div>
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 rounded-3xl overflow-hidden border border-slate-200 h-[420px]">
              <iframe
                title="Britelines service area Adelaide"
                src="https://www.google.com/maps?q=Adelaide,+South+Australia&z=11&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#475569] mb-6">We proudly service suburbs right across greater Adelaide, including:</p>
              <div className="flex flex-wrap gap-2.5">
                {SUBURBS.map((s) => (
                  <span key={s} className="text-sm px-4 py-2 rounded-full bg-[#E8F1EB] text-[#1C2A39]">{s}</span>
                ))}
              </div>
              <p className="text-sm text-[#475569] mt-6">Not listed? Get in touch — we likely cover your area too.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
